import { useState, useEffect, useRef } from 'react';
import type { RadioProgram, ProgramType } from '@/mocks/adminSchedule';
import { programTypeLabels, programTypeColors } from '@/mocks/adminSchedule';
import { SwalAlert } from '@/hooks/useSweetAlert';
import AdminImageUpload from '@/components/base/AdminImageUpload';

interface ProgramFormModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (program: Partial<RadioProgram>) => void;
  program?: RadioProgram | null;
  prefillName?: string;
}

const types: ProgramType[] = ['musica', 'programa_vivo', 'predicacion', 'entrevista', 'noticias', 'deportes', 'especial', 'evento'];

function timeToMinutes(time: string): number {
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
}

export default function ProgramFormModal({ open, onClose, onSave, program, prefillName }: ProgramFormModalProps) {
  const [form, setForm] = useState({
    name: '',
    host: '',
    description: '',
    dayOfWeek: 1,
    startTime: '08:00',
    endTime: '10:00',
    type: 'musica' as ProgramType,
    isLive: false,
    isRecurring: true,
    image: '',
  });

  const [saving, setSaving] = useState(false);
  const imageBlobRef = useRef<string | null>(null);

  useEffect(() => {
    if (program) {
      setForm({
        name: program.name,
        host: program.host,
        description: program.description,
        dayOfWeek: program.dayOfWeek,
        startTime: program.startTime,
        endTime: program.endTime,
        type: program.type,
        isLive: program.isLive,
        isRecurring: program.isRecurring,
        image: program.image || '',
      });
    } else {
      setForm({
        name: prefillName || '',
        host: '',
        description: '',
        dayOfWeek: 1,
        startTime: '08:00',
        endTime: '10:00',
        type: 'musica',
        isLive: false,
        isRecurring: true,
        image: '',
      });
    }
  }, [program, open, prefillName]);

  if (!open) return null;

  const nameOk = form.name.trim().length > 0;
  const hostOk = form.host.trim().length > 0;
  const descriptionOk = form.description.trim().length > 0;
  const startOk = form.startTime.length === 5;
  const endOk = form.endTime.length === 5;
  const timesOk = timeToMinutes(form.endTime) > timeToMinutes(form.startTime);
  const isValid = nameOk && hostOk && descriptionOk && startOk && endOk && timesOk;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!nameOk) {
      await SwalAlert.warning('Validación', 'El nombre del programa es requerido.');
      return;
    }
    if (!hostOk) {
      await SwalAlert.warning('Validación', 'El locutor es requerido.');
      return;
    }
    if (!startOk) {
      await SwalAlert.warning('Validación', 'La hora de inicio es requerida.');
      return;
    }
    if (!endOk) {
      await SwalAlert.warning('Validación', 'La hora de fin es requerida.');
      return;
    }
    if (!timesOk) {
      await SwalAlert.warning('Validación', 'La hora de fin debe ser mayor a la hora de inicio.');
      return;
    }

    setSaving(true);
    onSave({
      ...form,
      id: program?.id || Date.now(),
      hostAvatar: program?.hostAvatar || 'https://readdy.ai/api/search-image?query=Default%20radio%20host%20avatar%20portrait%2C%20friendly%20professional%20look%2C%20simple%20studio%20background%2C%20media%20headshot%20photography&width=80&height=80&seq=sched-host-default&orientation=squarish',
    });
    onClose();
    setSaving(false);
    await SwalAlert.toast(program ? 'Programa actualizado.' : 'Programa creado.', 'success');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>

      <div className="relative bg-background-50 rounded-2xl w-full max-w-lg mx-4 max-h-[85vh] overflow-y-auto shadow-xl border border-background-200/70">
        {/* Header */}
        <div className="sticky top-0 bg-background-50 border-b border-background-200/70 px-5 py-4 flex items-center justify-between z-10 rounded-t-2xl">
          <div>
            <h3 className="text-foreground-950 font-bold text-base">
              {program ? 'Editar programa' : 'Nuevo programa'}
            </h3>
            <p className="text-foreground-400 text-xs">Completá los campos para {program ? 'modificar' : 'agregar'} un programa</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors">
            <i className="ri-close-line text-foreground-500"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {/* Name */}
          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">
              Nombre del programa <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg bg-background-100 border text-foreground-800 text-sm outline-none transition-colors ${
                !nameOk && form.name !== '' ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-primary-300'
              }`}
              placeholder="Ej: Despierta con Energía"
            />
            {!nameOk && form.name !== '' && (
              <p className="text-red-500 text-[10px] mt-1">El nombre es requerido</p>
            )}
          </div>

          {/* Host */}
          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">
              Locutor <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.host}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
              className={`w-full px-3 py-2 rounded-lg bg-background-100 border text-foreground-800 text-sm outline-none transition-colors ${
                !hostOk && form.host !== '' ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-primary-300'
              }`}
              placeholder="Ej: Carlos Mendoza"
            />
            {!hostOk && form.host !== '' && (
              <p className="text-red-500 text-[10px] mt-1">El locutor es requerido</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Descripción</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors resize-none"
              rows={3}
              placeholder="Describí el programa..."
              maxLength={500}
            ></textarea>
            <p className="text-foreground-300 text-[10px] mt-1 text-right">{form.description.length}/500</p>
          </div>

          {/* Image */}
          <div>
            <AdminImageUpload
              label="Imagen del programa"
              currentImage={form.image}
              onChange={(file) => {
                if (imageBlobRef.current) URL.revokeObjectURL(imageBlobRef.current);
                const blobUrl = URL.createObjectURL(file);
                imageBlobRef.current = blobUrl;
                setForm({ ...form, image: blobUrl });
                SwalAlert.toast('Imagen cargada correctamente.', 'success');
              }}
              onRemove={() => {
                if (imageBlobRef.current) URL.revokeObjectURL(imageBlobRef.current);
                imageBlobRef.current = null;
                setForm({ ...form, image: '' });
              }}
              recommendedDimensions="800×450px"
              previewHeight="h-36"
              placeholderIcon="ri-image-add-line"
            />
          </div>

          {/* Day */}
          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Día</label>
            <select
              value={form.dayOfWeek}
              onChange={(e) => setForm({ ...form, dayOfWeek: parseInt(e.target.value) })}
              className="w-full px-3 py-2 rounded-lg bg-background-100 border border-background-200 text-foreground-800 text-sm outline-none focus:border-primary-300 transition-colors cursor-pointer"
            >
              <option value={0}>Domingo</option>
              <option value={1}>Lunes</option>
              <option value={2}>Martes</option>
              <option value={3}>Miércoles</option>
              <option value={4}>Jueves</option>
              <option value={5}>Viernes</option>
              <option value={6}>Sábado</option>
            </select>
          </div>

          {/* Time row */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-foreground-700 text-xs font-semibold mb-1.5">
                Hora inicio <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={form.startTime}
                onChange={(e) => setForm({ ...form, startTime: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg bg-background-100 border text-foreground-800 text-sm outline-none transition-colors ${
                  !startOk && form.startTime !== '' ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-primary-300'
                }`}
              />
            </div>
            <div>
              <label className="block text-foreground-700 text-xs font-semibold mb-1.5">
                Hora fin <span className="text-red-500">*</span>
              </label>
              <input
                type="time"
                value={form.endTime}
                onChange={(e) => setForm({ ...form, endTime: e.target.value })}
                className={`w-full px-3 py-2 rounded-lg bg-background-100 border text-foreground-800 text-sm outline-none transition-colors ${
                  (!endOk || !timesOk) && form.endTime !== '' ? 'border-red-300 focus:border-red-400' : 'border-background-200 focus:border-primary-300'
                }`}
              />
            </div>
          </div>

          {form.endTime && form.startTime && !timesOk && (
            <p className="text-red-500 text-xs">La hora de fin debe ser posterior a la hora de inicio</p>
          )}

          {/* Type */}
          <div>
            <label className="block text-foreground-700 text-xs font-semibold mb-1.5">Tipo</label>
            <div className="grid grid-cols-4 gap-1.5">
              {types.map((t) => {
                const colors = programTypeColors[t];
                return (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setForm({ ...form, type: t })}
                    className={`flex flex-col items-center gap-1 px-2 py-2 rounded-lg text-[10px] font-semibold cursor-pointer transition-all ${
                      form.type === t
                        ? 'ring-2 ring-offset-1 ring-primary-300 ' + colors.bg + ' ' + colors.text
                        : colors.bg + ' ' + colors.text + ' opacity-70 hover:opacity-100'
                    }`}
                  >
                    <i className={`${colors.icon} text-sm`}></i>
                    {programTypeLabels[t]}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6 pt-1">
            <label className="flex items-center gap-2 cursor-pointer">
              <div className={`w-9 h-5 rounded-full transition-colors ${form.isLive ? 'bg-emerald-500' : 'bg-background-300'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform mt-0.5 ${form.isLive ? 'translate-x-4' : 'translate-x-0.5'}`}></div>
              </div>
              <span className="text-foreground-700 text-xs font-medium">En vivo</span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <div className={`w-9 h-5 rounded-full transition-colors ${form.isRecurring ? 'bg-primary-500' : 'bg-background-300'}`}>
                <div className={`w-4 h-4 rounded-full bg-white shadow-sm transition-transform mt-0.5 ${form.isRecurring ? 'translate-x-4' : 'translate-x-0.5'}`}></div>
              </div>
              <span className="text-foreground-700 text-xs font-medium">Recurrente</span>
            </label>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 pt-3 border-t border-background-200/70">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-600 font-semibold text-sm cursor-pointer hover:bg-background-200 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className={`flex-1 px-4 py-2.5 rounded-xl font-semibold text-sm cursor-pointer transition-all whitespace-nowrap ${
                isValid
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'bg-primary-200 text-primary-400 cursor-not-allowed'
              }`}
            >
              {saving ? 'Guardando…' : program ? 'Guardar cambios' : 'Crear programa'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}